/**
 * Hourly multi-zone space heating/cooling demand simulation.
 *
 * One thermal zone is created per {@link EnergyZoneActivity}. Each hour the
 * model balances transmission, infiltration, ventilation, internal gains and
 * solar insolation against zone setpoints, then integrates indoor temperature
 * with an effective heat capacity (C_eff).
 *
 * Building-level results are area-weighted to Wh/m² of heated area. Coil
 * energy (post heating / pre cooling), regulation losses and airing are added
 * on the building total after the hourly loop.
 *
 * Related modules:
 * - {@link zone-schedule.helpers} — schedule matching and setpoint resolution
 * - {@link window-share.helpers} — distributing building windows across zones
 * - {@link space-demand.models} — input/output types and empty accumulators
 */

import { Baseline, ProjectMetadata } from '@shared/models/project.model';
import { IProposal } from '@shared/models/proposals.model';
import { BuildingDesign } from '@shared/models/design/building-design.model';
import { Replace, RequireProperties } from '@shared/models/type-helpers.model';
import { COORDINATE_STOCKHOLM } from '@shared/constants';
import { getWindowArea } from '@shared/templates/categories/windows-doors';
import { getCombinedAhuPropertiesFromVersion } from '@shared/templates/categories/air-handling-unit/air-handling-unit.helpers';
import { getCoolingMaxCapacityInVersion } from '@shared/templates/categories/cooling/cooling.helpers';
import { HOURS_PER_YEAR } from '@shared/constants/math.constants';
import {
  AIR_LEAKAGE_CLIMATE_FACTOR,
  AIR_LEAKAGE_Q50,
  AIRING_HEATING_COMSUMPTION_PER_HEATED_AREA,
  DEFAULT_INDOOR_TEMP,
  DEFAULT_VENT_FLOW_PER_M2,
  REGULATION_LOSS_FACTOR,
  SOLAR_HEAT_GAIN_COEFFICIENT,
  SOLAR_SCREEN_FACTOR,
  TAP_WATER_RECIRCULATION_LOSS_HEATING_GAIN_FACTOR,
  THERMAL_BRIDGES_LOSS_FACTOR,
  WINDOW_GLASS_AREA_PERCENTAGE,
} from './energy.models';
import {
  getHourSurfaceInsolationInW,
  toOrientedInsolationSurface,
} from './weather.helpers';
import {
  getBuildingEnvelopeArea,
  getTransmissionLossInWPerDegree,
} from './thermal-transmittance.helpers';
import { getAirflowHeatLoss_WPerDegree } from './ventilation-energy.helpers';
import { getBuildingEnvelopeElements } from './building-envelope.helpers';
import { getHeatingEffectInWPerOccupant } from './occupant-energy.helpers';
import { EnvironmentHourData, InsolationSurface } from './weather.models';
import {
  EnergyZoneActivity,
  getEnergyActivitiesFromDesign,
  getHeatedAreaFromEnergyActivities,
} from './energy-activity.helpers';
import { ActivityType } from '@shared/models/design/activities/activity-type.model';
import { isResidentialProgramType } from '@shared/models/design/activities/properties';
import {
  getTapwaterHeatingDemandByActivity,
  getTapWaterRecirculationLossInW,
  getTapWaterPipeLength,
} from './energy.helpers';
import {
  getActivityGainInWPerM2,
  toEnergyActivity,
} from './energy-activity-type.helpers';
import { computeWindowShares } from './window-share.helpers';
import {
  buildZonePropsTimeline,
  ResolvedZoneProps,
  ScheduleLayerTimelineCache,
  SimulationTime,
  ZonePropsTimelineCache,
} from './zone-schedule.helpers';
import {
  BuildingAnnual,
  createEmptySpaceDemandResult,
  DemandMode,
  EMPTY_BUILDING_ANNUAL,
  EMPTY_ZONE_ANNUAL,
  PostHeatingStrategy,
  PreCoolingStrategy,
  SpaceDemandOptions,
  SpaceDemandResult,
  SpaceDemandVentilationOptions,
  ZoneAnnual,
  ZoneState,
} from './space-demand.models';
import { sum } from '../math.helpers';
import { timeZoneHourNumberApprox } from '../date.helpers';
import { required, assertUnreachable, cacheFactory } from '../function.helpers';
import { guards } from '../typeguards.helpers';
import { convert } from '../conversion.helpers';
import { migrateProjectMetadataToBuildingDesign } from '../design/migration/design-migrations.helpers';

/** Simulation time step: one hour expressed as seconds and as hours */
const DT_S = 3600; // s
const DT_H = 1; // h

/** Default effective thermal capacity per heated area, J/(m²·K) */
const DEFAULT_C_EFF = 120_000;

/**
 * Power above this threshold (W/m²) counts as an active heating/cooling hour.
 * Used to derive heatingHours, coolingHours and limitTemp on building level.
 */
const ACTIVE_POWER_THRESHOLD_W_PER_M2 = 1e-3;

/** Keys iterated when area-weighting zone annuals to building totals */
const ZONE_ANNUAL_KEYS = Object.keys(EMPTY_ZONE_ANNUAL) as (keyof ZoneAnnual)[];

/**
 * True when the hour has usable solar irradiance for Perez transposition.
 * Skips expensive insolation work on night hours without changing results.
 * @param hour Environment hour
 * @returns Whether solar insolation should be calculated
 */
const hasSolarIrradiance = (hour: EnvironmentHourData): boolean =>
  hour.solarElevationRad > 0 &&
  (hour['Gb(n)'] > 0 || hour['Gd(h)'] > 0 || hour['G(h)'] > 0);

/**
 * Constant per-simulation configuration shared by all zones and hours.
 * Built once in {@link getSpaceDemandInWhPerM2} and passed to every
 * {@link simulateZoneHour} call.
 */
type ZoneSimulationConfig = {
  ventilation: SpaceDemandVentilationOptions;
  /** Fallback vent flow when the zone has none (m³/s·m²) */
  defaultVentFlowPerM2: number;
  /** Air leakage losses (W/m²·K) */
  airLeakageWPerM2K: number;
  /** Global heating power cap (W/m²) */
  heatPowerLimitWPerM2: number;
  /** Global cooling power cap (W/m²) */
  coolPowerLimitWPerM2: number;
  /** Hysteresis before bypassing heat recovery for free cooling (K) */
  hrBypassHysteresisK: number;
  /** Effective thermal capacity, J/(m²·K) */
  cEffJPerM2K: number;
  /** Tap water recirculation gain (W/m²) */
  tapWaterGainWPerM2: number;
  /** Total building window area (m²) */
  buildingWindowArea: number;
};

/**
 * Per-hour, per-zone outcome aggregated on building level.
 * Degree hours are summed across all zones (not area-weighted).
 */
type ZoneHourResult = {
  /** True when post heating or terminal heating exceeds the active threshold */
  heatActive: boolean;
  /** True when pre cooling or terminal cooling exceeds the active threshold */
  coolActive: boolean;
  /** Heating degree hours this hour (K·h), based on outdoor vs heat setpoint */
  degreeHoursHeat: number;
  /** Cooling degree hours this hour (K·h), based on outdoor vs cool setpoint */
  degreeHoursCool: number;
};

/**
 * Create initial zone states (one zone per activity) with window shares.
 * @param activities Energy zone activities
 * @param insolationSurfaces Building window surfaces
 * @param buildingTransmissionWPerM2K Building per-m² transmission coefficient (fallback per zone)
 * @param startTime Simulation clock at the first weather hour
 * @param scheduleLayerCache Shared schedule layer cache across zones
 * @param zonePropsCache Shared resolved props timeline cache for identical zones
 * @returns Mutable zone states, see {@link ZoneState}
 */
const createZoneStates = (
  activities: EnergyZoneActivity[],
  insolationSurfaces: InsolationSurface[],
  buildingTransmissionWPerM2K: number,
  startTime: SimulationTime,
  scheduleLayerCache: ScheduleLayerTimelineCache,
  zonePropsCache: ZonePropsTimelineCache,
): ZoneState[] => {
  const windowShares = computeWindowShares(activities, insolationSurfaces);

  return activities.map((act, index) => ({
    act,
    area: act.area,
    tempIn: DEFAULT_INDOOR_TEMP,
    Htr_WperK_m2: act.properties.Htr_WperK_m2 ?? buildingTransmissionWPerM2K,
    annual: { ...EMPTY_ZONE_ANNUAL },
    windowShare: windowShares[index] ?? 0,
    // Precompute once so the hourly loop avoids re-orienting the windows
    orientedWindows: act.windows?.length
      ? toOrientedInsolationSurface(act.windows)
      : undefined,
    // Precompute schedule resolution once per zone (shared across identical zones/schedules)
    propsTimeline: buildZonePropsTimeline(
      act,
      startTime,
      scheduleLayerCache,
      zonePropsCache,
    ),
    occupantGainWPerOccupant: getHeatingEffectInWPerOccupant(act.type),
    activityGainWPerM2Base: getActivityGainInWPerM2(act.type),
  }));
};

/**
 * Determine whether the zone wants heating, cooling or nothing this hour.
 * @param tempIn Current indoor temperature (°C)
 * @param heatSet Heating setpoint (°C)
 * @param coolSet Cooling setpoint (°C)
 * @returns Demand mode, see {@link DemandMode}
 */
const getZoneDemandMode = (
  tempIn: number,
  heatSet: number,
  coolSet: number,
): DemandMode => {
  if (tempIn < heatSet) return 'heat';
  if (tempIn > coolSet) return 'cool';
  return 'off';
};

/**
 * Get the temperature the zone is controlled towards given the demand mode.
 * @param mode Demand mode for the hour
 * @param options Setpoints and current indoor temperature (°C)
 * @returns Control temperature (°C)
 */
const getControlTemperature = (
  mode: DemandMode,
  options: { heatSet: number; coolSet: number; tempIn: number },
): number => {
  switch (mode) {
    case 'heat':
      return options.heatSet;
    case 'cool':
      return options.coolSet;
    case 'off':
      return options.tempIn;
    default:
      return assertUnreachable(mode);
  }
};

/**
 * Solar gain for a zone this hour. Zones with explicit windows use them
 * directly, other zones get their share of the building's solar gains.
 * @param zone Zone state
 * @param hour Environment hour
 * @param buildingSolarW Total building solar gain this hour (W)
 * @param buildingWindowArea Total building window area (m²)
 * @returns Solar gain (W/m² of zone area)
 */
const getZoneSolarGainInWPerM2 = (
  zone: ZoneState,
  hour: EnvironmentHourData,
  buildingSolarW: number,
  buildingWindowArea: number,
): number => {
  if (zone.orientedWindows) {
    // Skip Perez when the sun is down — same result as inside getHourSurfaceInsolationInW
    if (!hasSolarIrradiance(hour)) return 0;
    return (
      getHourSurfaceInsolationInW(hour, zone.orientedWindows, 'perez') /
      zone.area
    );
  }
  const share = buildingWindowArea > 0 ? zone.windowShare : 0;
  return (buildingSolarW * share) / zone.area;
};

/**
 * Input for {@link getSatCoilPowerInWPerM2}.
 * The coil is modelled as ρ·cp·V̇/A per kelvin — heat recovery is already
 * applied before tempAfterFan; the coil only bridges the remaining gap to
 * the SAT setpoint.
 */
type SatCoilPowerInput = {
  /** Current zone demand mode — controls strategy-gated coil activation */
  mode: DemandMode;
  /** Supply air temperature after heat recovery and fan (°C) */
  tempAfterFan: number;
  /** Coil capacity per kelvin (ρ·cp·V̇ / A), i.e. without HR (W/m²·K) */
  coilWPerM2K: number;
  /** Minimum SAT after the coil (°C) */
  postHeatingTemp?: number;
  /** Maximum SAT after the coil (°C) */
  preCoolingTemp?: number;
  /** When to apply post heating — see {@link PostHeatingStrategy} */
  postHeatingStrategy?: PostHeatingStrategy;
  /** When to apply pre cooling — see {@link PreCoolingStrategy} */
  preCoolingStrategy?: PreCoolingStrategy;
};

/**
 * Supply air temperature (SAT) coil power for post heating / pre cooling.
 * Positive result is post heating energy, negative is pre cooling.
 * @param input See {@link SatCoilPowerInput}
 * @returns Coil power (W/m², positive = post heating, negative = pre cooling)
 */
const getSatCoilPowerInWPerM2 = ({
  mode,
  tempAfterFan,
  coilWPerM2K,
  postHeatingTemp,
  preCoolingTemp,
  postHeatingStrategy,
  preCoolingStrategy,
}: SatCoilPowerInput): number => {
  let satPowerWm2 = 0;

  // Post heating (eftervärmare)
  if (
    guards.is.number(postHeatingTemp) &&
    guards.is.positiveNumber(postHeatingTemp)
  ) {
    const needHeatK = Math.max(0, postHeatingTemp - tempAfterFan);
    const shouldHeatSat =
      postHeatingStrategy === 'alwaysMinSAT' ? true : mode === 'heat';
    if (shouldHeatSat && needHeatK > 0) satPowerWm2 += coilWPerM2K * needHeatK; // ≥ 0
  }

  // Pre cooling (förkylning)
  if (
    guards.is.number(preCoolingTemp) &&
    guards.is.positiveNumber(preCoolingTemp)
  ) {
    const needCoolK = Math.min(0, preCoolingTemp - tempAfterFan);
    const shouldCoolSat =
      preCoolingStrategy === 'alwaysMaxSAT' ? true : mode === 'cool';
    if (shouldCoolSat && needCoolK < 0) satPowerWm2 += coilWPerM2K * needCoolK; // ≤ 0
  }

  return satPowerWm2;
};

/**
 * Simulate one hour for one zone.
 *
 * Energy balance per m² of zone area:
 *   p_req = (C_eff/dt)·(T_ctrl − T_in) + losses − gains
 * Terminal heating/cooling is capped by zone and global power limits.
 * Useful gains (person, activity, solar, tap water) are scaled so they
 * cannot offset more than the heating requirement at T_ctrl.
 *
 * Mutates {@link ZoneState.annual} accumulators and {@link ZoneState.tempIn}.
 *
 * @param zone Zone state (mutated)
 * @param hour Environment hour with outdoor temperature and solar data
 * @param hourIndex Index into {@link ZoneState.propsTimeline} (0–8759)
 * @param props Precomputed resolved zone properties for this hour
 * @param buildingSolarW Total building solar gain this hour (W)
 * @param config Simulation configuration, see {@link ZoneSimulationConfig}
 * @returns Per-hour outcome, see {@link ZoneHourResult}
 */
const simulateZoneHour = (
  zone: ZoneState,
  hour: EnvironmentHourData,
  hourIndex: number,
  props: ResolvedZoneProps,
  buildingSolarW: number,
  config: ZoneSimulationConfig,
): ZoneHourResult => {
  const { area, annual } = zone;
  const { ventilation, cEffJPerM2K } = config;
  const outdoorTemp = hour.T2m;

  const postHeatingTemp = props.postHeatingTemp ?? ventilation.postHeatingTemp;
  const preCoolingTemp = props.preCoolingTemp ?? ventilation.preCoolingTemp;

  // No comfort cooling system? -> disable cooling by pushing the effective cooling setpoint to +∞
  const coolPowerCapWm2 = props.coolPowerCap ?? config.coolPowerLimitWPerM2;
  const coolSet =
    coolPowerCapWm2 <= 0 ? Number.POSITIVE_INFINITY : props.coolSet;

  // Degree hours (K·h)
  const degreeHoursHeat = Math.max(0, props.heatSet - outdoorTemp) * DT_H;
  const degreeHoursCool = Math.max(0, outdoorTemp - coolSet) * DT_H;

  // Vent flow per m² of zone area
  const ventFlowPerM2 = props.ventFlowPerM2_m3s ?? config.defaultVentFlowPerM2;
  const ventFlowM3s = ventFlowPerM2 * area;

  // Internal gains (W/m² of zone area) — base gains precomputed at zone setup
  const occupantGainWm2 =
    zone.occupantGainWPerOccupant *
    (props.occupantCountPerM2 ?? 0) *
    props.occupantMultiplier;
  const activityGainWm2 =
    zone.activityGainWPerM2Base * props.activityMultiplier;

  const deltaT = zone.tempIn - outdoorTemp;
  const mode = getZoneDemandMode(zone.tempIn, props.heatSet, coolSet);

  // Bypass HR when cooling is wanted and outdoor is colder than indoor (free cooling)
  const wantBypass =
    mode === 'cool' && outdoorTemp < zone.tempIn - config.hrBypassHysteresisK;
  const usedHeatRecovery = wantBypass ? 0 : ventilation.heatRecoveryEfficiency;
  // Compute airflow heat loss once; reused for ventilation loss and SAT coil capacity
  const airflowLossWPerK = getAirflowHeatLoss_WPerDegree(ventFlowM3s, 0);
  const ventilationWPerM2K = (airflowLossWPerK * (1 - usedHeatRecovery)) / area;

  const transmissionWPerM2K = zone.Htr_WperK_m2;
  const airLeakageWPerM2K = config.airLeakageWPerM2K;
  const totalLossWPerM2K =
    transmissionWPerM2K + airLeakageWPerM2K + ventilationWPerM2K;

  // Total losses (W/m²)
  const lossWm2 = deltaT * totalLossWPerM2K;

  const solarGainWm2 = getZoneSolarGainInWPerM2(
    zone,
    hour,
    buildingSolarW,
    config.buildingWindowArea,
  );
  const tapWaterGainWm2 = config.tapWaterGainWPerM2;

  // Supply air after heat recovery (+ fan heat rise)
  const tempAfterHR =
    outdoorTemp + usedHeatRecovery * (zone.tempIn - outdoorTemp);
  const tempAfterFan = tempAfterHR + (ventilation.fanHeatRiseK || 0);

  // SAT coil power (positive = post heating, negative = pre cooling)
  const satPowerWm2 = getSatCoilPowerInWPerM2({
    mode,
    tempAfterFan,
    coilWPerM2K: airflowLossWPerK / area,
    postHeatingTemp,
    preCoolingTemp,
    postHeatingStrategy: ventilation.postHeatingStrategy,
    preCoolingStrategy: ventilation.preCoolingStrategy,
  });

  // Total gains (W/m²)
  const gainsWm2 =
    occupantGainWm2 +
    activityGainWm2 +
    solarGainWm2 +
    tapWaterGainWm2 +
    satPowerWm2;

  // Log gains
  annual.occupant += occupantGainWm2 * DT_H;
  annual.activity += activityGainWm2 * DT_H;
  annual.insolation += solarGainWm2 * DT_H;
  annual.tapwater += tapWaterGainWm2 * DT_H;
  // Log coil energy separately
  annual.postHeat += Math.max(0, satPowerWm2) * DT_H;
  annual.preCool += Math.max(0, -satPowerWm2) * DT_H;

  // Control towards T_ctrl depending on deadband mode
  const controlTemp = getControlTemperature(mode, {
    heatSet: props.heatSet,
    coolSet,
    tempIn: zone.tempIn,
  });

  // Useful gains: how much of the gains offsets the heating requirement
  const requiredPowerWithoutGainsWm2 =
    (cEffJPerM2K / DT_S) * (controlTemp - zone.tempIn) +
    deltaT * totalLossWPerM2K;

  const usefulBudgetWm2 = Math.max(0, requiredPowerWithoutGainsWm2); // max useful
  const totalGainsWm2 = Math.max(
    0,
    occupantGainWm2 + activityGainWm2 + solarGainWm2 + tapWaterGainWm2,
  );
  const usefulFraction =
    totalGainsWm2 > 0 ? Math.min(1, usefulBudgetWm2 / totalGainsWm2) : 0;

  annual.occupantHeat += occupantGainWm2 * usefulFraction * DT_H;
  annual.activityHeat += activityGainWm2 * usefulFraction * DT_H;
  annual.insolationHeat += solarGainWm2 * usefulFraction * DT_H;
  annual.tapwaterHeat += tapWaterGainWm2 * usefulFraction * DT_H;

  // Losses — signed (diagnostics)
  annual.transmission += transmissionWPerM2K * deltaT * DT_H;
  annual.airleakage += airLeakageWPerM2K * deltaT * DT_H;
  annual.ventilation += ventilationWPerM2K * deltaT * DT_H;

  // Losses — split per direction (≥ 0)
  const deltaTHeat = Math.max(deltaT, 0); // Tin > Tout
  const deltaTCool = Math.max(-deltaT, 0); // Tin < Tout

  annual.transmissionHeat += transmissionWPerM2K * deltaTHeat * DT_H;
  annual.transmissionCool += transmissionWPerM2K * deltaTCool * DT_H;

  annual.airleakageHeat += airLeakageWPerM2K * deltaTHeat * DT_H;
  annual.airleakageCool += airLeakageWPerM2K * deltaTCool * DT_H;

  annual.ventilationHeat += ventilationWPerM2K * deltaTHeat * DT_H;
  annual.ventilationCool += ventilationWPerM2K * deltaTCool * DT_H;

  // Required terminal power to reach the control temperature
  const requiredPowerWm2 =
    (cEffJPerM2K / DT_S) * (controlTemp - zone.tempIn) + (lossWm2 - gainsWm2);

  // Zone-specific power caps (fallback to global caps)
  const heatCapWm2 = props.heatPowerCap ?? config.heatPowerLimitWPerM2;

  const heatDemandWm2 = mode === 'heat' ? Math.max(0, requiredPowerWm2) : 0;
  const coolDemandWm2 = mode === 'cool' ? Math.max(0, -requiredPowerWm2) : 0;

  const heatDeliveredWm2 = Math.min(heatDemandWm2, heatCapWm2);
  const coolDeliveredWm2 = Math.min(coolDemandWm2, coolPowerCapWm2);

  // Energy
  annual.heat += heatDeliveredWm2 * DT_H;
  annual.cool += coolDeliveredWm2 * DT_H;
  annual.unmetHeat += (heatDemandWm2 - heatDeliveredWm2) * DT_H;
  annual.unmetCool += (coolDemandWm2 - coolDeliveredWm2) * DT_H;

  // State: integrate the indoor temperature
  zone.tempIn +=
    (DT_S / cEffJPerM2K) *
    (gainsWm2 - lossWm2 + heatDeliveredWm2 - coolDeliveredWm2);

  return {
    heatActive:
      /* post heating */ satPowerWm2 > ACTIVE_POWER_THRESHOLD_W_PER_M2 ||
      /* terminal heating */ heatDeliveredWm2 > ACTIVE_POWER_THRESHOLD_W_PER_M2,
    coolActive:
      /* pre cooling */ satPowerWm2 < -ACTIVE_POWER_THRESHOLD_W_PER_M2 ||
      /* terminal cooling */ coolDeliveredWm2 > ACTIVE_POWER_THRESHOLD_W_PER_M2,
    degreeHoursHeat,
    degreeHoursCool,
  };
};

/**
 * Input for {@link aggregateBuildingAnnual}.
 * Zone annuals are per m² of zone area; the output is per m² of heated area.
 */
type AggregateBuildingAnnualInput = {
  zones: ZoneState[];
  heatedArea: number;
  /** Airing loss for the year (Wh/m²), zeroed when there is no heating season */
  airingWhPerM2: number;
  /** Fraction of delivered heat lost to regulation (e.g. 0.1 = 10 %) */
  regulationLossFactor: number;
  /** Number of hours where any zone delivered heat */
  heatingHours: number;
  /** Sum of zone degree hours heat over the year (K·h) */
  degreeHoursHeat: number;
  /** Sum of zone degree hours cool over the year (K·h) */
  degreeHoursCool: number;
};

/**
 * Aggregate zone results into building totals (Wh/m² of heated area).
 *
 * Post-processing order:
 * 1. Area-weight zone annuals
 * 2. Add postHeat into heat, preCool into cool
 * 3. Apply regulation loss on heat (before airing)
 * 4. Add airing (only when heatingHours > 0)
 *
 * @param input See {@link AggregateBuildingAnnualInput}
 * @returns Building annual results, see {@link BuildingAnnual}
 */
const aggregateBuildingAnnual = ({
  zones,
  heatedArea,
  airingWhPerM2,
  regulationLossFactor,
  heatingHours,
  degreeHoursHeat,
  degreeHoursCool,
}: AggregateBuildingAnnualInput): BuildingAnnual => {
  const total: BuildingAnnual = {
    ...EMPTY_BUILDING_ANNUAL,
    degreeHoursHeat,
    degreeHoursCool,
  };

  // Area-weighted totals (Wh/m² of heated area)
  for (const zone of zones) {
    const areaFraction = zone.area / heatedArea;
    for (const key of ZONE_ANNUAL_KEYS) {
      total[key] += zone.annual[key] * areaFraction;
    }
  }

  // Include coil energy (post heating and pre cooling) in totals
  total.heat += total.postHeat;
  total.cool += total.preCool;

  // Regulation loss only on controlled heat (terminal + post heating), not on airing
  total.regulation = total.heat * regulationLossFactor;

  // Airing (zeroed when there is no heating season)
  total.airing = heatingHours === 0 ? 0 : airingWhPerM2;

  // Total delivered heat
  total.heat += total.regulation + total.airing;

  return total;
};

/**
 * Hourly multi-zone space demand simulation.
 *
 * Requires exactly {@link HOURS_PER_YEAR} weather hours. Returns an empty
 * result when there are no activities or no heated area.
 *
 * @param options Simulation options, see {@link SpaceDemandOptions}
 * @returns Space demand result with per-zone and building annuals (Wh/m²)
 */
export const getSpaceDemandInWhPerM2 = ({
  environmentHours,
  activities,
  ventilation,
  insolationSurfaces,
  airingLossInKwhPerHeatedArea = AIRING_HEATING_COMSUMPTION_PER_HEATED_AREA,
  tapWaterRecirculationGainInWPerHeatedArea = 0,
  regulationLossFactor = REGULATION_LOSS_FACTOR,
  C_eff = DEFAULT_C_EFF,
  heatPowerLimitWPerM2 = Number.POSITIVE_INFINITY,
  coolPowerLimitWPerM2 = Number.POSITIVE_INFINITY,
  hrBypassHysteresisK = 0.2, // K
  ventFlow = DEFAULT_VENT_FLOW_PER_M2,
  buildingCoordinate = COORDINATE_STOCKHOLM,
  airLeakageLossesWPerM2K,
  transmissionLossesWPerM2K,
}: SpaceDemandOptions): SpaceDemandResult => {
  if (environmentHours.length !== HOURS_PER_YEAR)
    throw new Error('weatherHours != 8760');

  if (!activities?.length)
    return createEmptySpaceDemandResult(transmissionLossesWPerM2K);

  const heatedArea = getHeatedAreaFromEnergyActivities(activities);

  if (!(heatedArea > 0))
    return createEmptySpaceDemandResult(transmissionLossesWPerM2K);

  const startTime: SimulationTime = {
    hour: timeZoneHourNumberApprox(buildingCoordinate),
    weekday: 1,
    week: 1,
  };
  const scheduleLayerCache: ScheduleLayerTimelineCache = new Map();
  const zonePropsCache: ZonePropsTimelineCache = new Map();

  // Zones are created from activities (one zone per activity)
  const zones = createZoneStates(
    activities,
    insolationSurfaces,
    transmissionLossesWPerM2K,
    startTime,
    scheduleLayerCache,
    zonePropsCache,
  );

  const config: ZoneSimulationConfig = {
    ventilation,
    defaultVentFlowPerM2: ventFlow,
    airLeakageWPerM2K: airLeakageLossesWPerM2K,
    heatPowerLimitWPerM2,
    coolPowerLimitWPerM2,
    hrBypassHysteresisK,
    cEffJPerM2K: C_eff,
    tapWaterGainWPerM2: tapWaterRecirculationGainInWPerHeatedArea,
    buildingWindowArea: sum(insolationSurfaces, 'area'),
  };

  // Building-level meta
  let heatingHours = 0;
  let coolingHours = 0;
  let limitTemp = Number.NEGATIVE_INFINITY;
  let degreeHoursHeat = 0;
  let degreeHoursCool = 0;

  const orientedInsolationSurfaces =
    toOrientedInsolationSurface(insolationSurfaces);

  for (let hourIndex = 0; hourIndex < environmentHours.length; hourIndex++) {
    const hour = environmentHours[hourIndex]!;
    // Skip Perez on night hours — identical to getHourSurfaceInsolationInW early exit
    const buildingSolarW = hasSolarIrradiance(hour)
      ? getHourSurfaceInsolationInW(hour, orientedInsolationSurfaces, 'perez')
      : 0;

    let anyHeat = false;
    let anyCool = false;

    for (const zone of zones) {
      const result = simulateZoneHour(
        zone,
        hour,
        hourIndex,
        zone.propsTimeline[hourIndex]!,
        buildingSolarW,
        config,
      );
      anyHeat ||= result.heatActive;
      anyCool ||= result.coolActive;
      degreeHoursHeat += result.degreeHoursHeat;
      degreeHoursCool += result.degreeHoursCool;
    }

    if (anyHeat) {
      heatingHours++;
      limitTemp = Math.max(limitTemp, hour.T2m);
    }
    if (anyCool) {
      coolingHours++;
    }
  }

  const annualWhPerM2 = aggregateBuildingAnnual({
    zones,
    heatedArea,
    airingWhPerM2: convert(airingLossInKwhPerHeatedArea, 'kWh', 'Wh'),
    regulationLossFactor,
    heatingHours,
    degreeHoursHeat,
    degreeHoursCool,
  });

  return {
    heatedArea,
    zones: zones.map((zone) => ({
      activity: zone.act.type,
      area: zone.area,
      finalTin_C: zone.tempIn,
      Htr_WperK_m2: zone.Htr_WperK_m2,
      annualWhPerM2: zone.annual, // per m² of zone area
    })),
    building: {
      Um: transmissionLossesWPerM2K, // the building's per-m² transmission
      limitTemp: heatingHours > 0 ? limitTemp : null,
      heatingHours,
      coolingHours,
      annualWhPerM2, // per m² of heated area
    },
  };
};

/**
 * Air leakage heat loss coefficient for the building envelope.
 * @param envelopeArea Building envelope area (m²)
 * @param airLeakageQ50InLPerS Air leakage at 50 Pa (l/s·m²)
 * @param e Climate adjustment factor, see {@link AIR_LEAKAGE_CLIMATE_FACTOR}
 * @returns Air leakage loss (W/K)
 */
export const getAirLeakageLossInWPerDegree = (
  envelopeArea: number,
  airLeakageQ50InLPerS = AIR_LEAKAGE_Q50,
  e = AIR_LEAKAGE_CLIMATE_FACTOR,
): number => {
  const airLeakageFlowInm3PerS =
    (airLeakageQ50InLPerS * envelopeArea * e) / 1000; // convert l/s -> m³/s
  // Calculated as intentional ventilation, but with 0 heat recovery
  return getAirflowHeatLoss_WPerDegree(airLeakageFlowInm3PerS, 0);
};

type GetSpaceDemandOptionsFromVersionInputBase = Replace<
  Omit<
    SpaceDemandOptions,
    | 'transmissionLossesWPerM2K'
    | 'airLeakageLossesWPerM2K'
    | 'activities'
    | 'tapWaterRecirculationGainInWPerHeatedArea'
    | 'airingLossInKwhPerHeatedArea'
  >,
  {
    insolationSurfaces?: InsolationSurface[];
    metadata?: ProjectMetadata;

    /**
     * Optional building design (geometry 2.0). If provided, activities and areas will be taken from design.activities and design.storeys
     */
    design?: BuildingDesign;

    ventilation?: Pick<
      SpaceDemandVentilationOptions,
      'postHeatingStrategy' | 'preCoolingStrategy' | 'fanHeatRiseK'
    >;
    /** When set, envelope/transmission uses only elements active in this proposal (for per-proposal heat demand). */
    proposal?: IProposal;
  }
>;

type SpaceDemandOptionsFromVersion = RequireProperties<
  SpaceDemandOptions,
  | 'tapWaterRecirculationGainInWPerHeatedArea'
  | 'airingLossInKwhPerHeatedArea'
  | 'transmissionLossesWPerM2K'
  | 'airLeakageLossesWPerM2K'
> & {
  tapwaterHeatingDemandKwhPerHeatedArea: number;

  /**
   * Just for display purpose
   */
  tapWaterRecirculationLossesInWPerHeatedArea?: number;
};

/**
 * Options needed to get all options to getSpaceDemandInWhPerM2 from building version
 */
type GetSpaceDemandOptionsFromVersionInput =
  // Either get space activities from design
  | RequireProperties<GetSpaceDemandOptionsFromVersionInputBase, 'design'>
  // OR derive design from metadata
  | RequireProperties<GetSpaceDemandOptionsFromVersionInputBase, 'metadata'>;

/**
 * Build the full {@link SpaceDemandOptions} for {@link getSpaceDemandInWhPerM2}
 * from a building version (envelope, ventilation, activities, tap water).
 * @param version Building version
 * @param options Input options, see {@link GetSpaceDemandOptionsFromVersionInput}
 * @returns Resolved space demand options
 */
const computeSpaceDemandOptionsFromVersion = (
  version: Baseline,
  options: GetSpaceDemandOptionsFromVersionInput,
): SpaceDemandOptionsFromVersion => {
  const {
    environmentHours,
    thermalBridgesLossFactor = THERMAL_BRIDGES_LOSS_FACTOR,
    airLeakageQ50InLPerS = AIR_LEAKAGE_Q50,
    metadata,
    design,
    ventilation = {},
    proposal,
  } = options;

  // Apply default insolation surfaces if not provided
  const insolationSurfaces: SpaceDemandOptions['insolationSurfaces'] = options
    .insolationSurfaces?.length
    ? options.insolationSurfaces
    : [
        {
          area: getWindowArea(version),
          factor:
            WINDOW_GLASS_AREA_PERCENTAGE *
            SOLAR_SCREEN_FACTOR *
            SOLAR_HEAT_GAIN_COEFFICIENT,
        },
      ];

  const designForEnergy =
    design ??
    migrateProjectMetadataToBuildingDesign(
      required(metadata, 'metadata or design must be provided'),
    );
  const activities = getEnergyActivitiesFromDesign(designForEnergy);
  const heatedArea = getHeatedAreaFromEnergyActivities(activities);
  const ventProps = getCombinedAhuPropertiesFromVersion(version);
  const coolPowerLimitWPerM2 =
    heatedArea > 0
      ? convert(getCoolingMaxCapacityInVersion(version), 'kW', 'W') / heatedArea
      : 0;

  const ventilationOptions: SpaceDemandOptions['ventilation'] = {
    ...ventilation,
    ventFlowPerM2:
      ventProps.airflow / ventProps.ventilationArea || DEFAULT_VENT_FLOW_PER_M2, // Fallback when 0 vent area
    area: heatedArea,
    heatRecoveryEfficiency: ventProps.heatRecoveryEfficiency,
    postHeatingTemp: ventProps.postHeatingTemp,
  };

  // The building's default per-m² envelope (proposal filters envelope when computing per-proposal heat demand)
  const transmissionLossesWPerM2K =
    getTransmissionLossInWPerDegree(
      version,
      thermalBridgesLossFactor,
      proposal,
    ) / heatedArea;

  const airLeakageLossesWPerM2K =
    getAirLeakageLossInWPerDegree(
      getBuildingEnvelopeArea(getBuildingEnvelopeElements(version, proposal)),
      airLeakageQ50InLPerS,
    ) / heatedArea;

  // TAPWATER HEATING DEMAND (per activity summed together)
  const tapwaterHeatingDemandKwhPerHeatedArea =
    sum(
      activities,
      (act) =>
        act.area *
        getTapwaterHeatingDemandByActivity(toEnergyActivity(act.type), {
          residentialProgramType:
            act.type === ActivityType.Residential &&
            isResidentialProgramType(act.programType)
              ? act.programType
              : undefined,
        }),
    ) / heatedArea;

  // RECIRCULATION LOSS
  const tapWaterRecirculationLossesInWPerHeatedArea =
    sum(activities, (act) =>
      getTapWaterRecirculationLossInW(
        getTapWaterPipeLength(act.area, toEnergyActivity(act.type)),
      ),
    ) / heatedArea;

  // Gains is losses * gain factor
  const tapWaterRecirculationGainInWPerHeatedArea =
    tapWaterRecirculationLossesInWPerHeatedArea *
    TAP_WATER_RECIRCULATION_LOSS_HEATING_GAIN_FACTOR;

  return {
    ventilation: ventilationOptions,
    insolationSurfaces,
    environmentHours,
    activities,
    transmissionLossesWPerM2K,
    airLeakageLossesWPerM2K,
    coolPowerLimitWPerM2,
    tapWaterRecirculationGainInWPerHeatedArea,
    tapWaterRecirculationLossesInWPerHeatedArea,
    tapwaterHeatingDemandKwhPerHeatedArea,
    airingLossInKwhPerHeatedArea: AIRING_HEATING_COMSUMPTION_PER_HEATED_AREA,
    buildingCoordinate: designForEnergy.location,
    thermalBridgesLossFactor,
    airLeakageQ50InLPerS,
  };
};

/**
 * Cached wrapper around {@link computeSpaceDemandOptionsFromVersion}.
 * Envelope/design extraction is expensive and unchanged between pipeline and UI calls.
 * @param version Building version
 * @param options Input options, see {@link GetSpaceDemandOptionsFromVersionInput}
 * @returns Resolved space demand options
 */
export const getSpaceDemandOptionsFromVersion = (
  version: Baseline,
  options: GetSpaceDemandOptionsFromVersionInput,
): SpaceDemandOptionsFromVersion => {
  const proposalId = options.proposal?.id ?? '';
  return cacheFactory(
    () => computeSpaceDemandOptionsFromVersion(version, options),
    `getSpaceDemandOptionsFromVersion[${version.id}][${proposalId}]`,
    [
      version,
      options.metadata,
      options.design,
      options.environmentHours,
      options.insolationSurfaces,
      options.thermalBridgesLossFactor,
      options.airLeakageQ50InLPerS,
      options.ventilation,
      options.proposal,
    ],
  );
};
